<template>
  <div class="machine-details">
    <!-- NAVBAR -->
    <HeaderMenu />
    <!-- HAUPTINHALT -->
    <section class="pb-5" id="machine-section">
      <div class="container machine-wrapper">
        <!-- Ladezustand -->
        <div v-if="loading" class="loading">
          {{ $t("loadingDetails") }}
        </div>
        <!-- Fehlermeldung -->
        <div v-else-if="error" class="error">
          <img
            :src="
              require('@/assets/images/sander/LMH_Icons_delete_128_black.png')
            "
            alt=""
            class="error-image mt-5 pt-2"
          />
          <h4 class="mb-3 mt-2 pb-3 pt-2 fs-4 fw-bold">{{ error }}</h4>

          <button @click="goBack" class="btn btn-primary back-button">
            {{ $t("t-cards-overview") }}
          </button>
        </div>
        <!-- Maschine gefunden -->
        <div
          v-else-if="machine"
          class="machine-details-container p-4 shadow-sm"
        >
          <div class="d-flex justify-content-between align-items-center mb-4">
            <h1 class="mb-0 machine-header">
              {{ machine.manufacturer }} {{ machine.model }}
            </h1>
            <div>
              <div class="action-container-top">
                <PdfDownloadButton :machine="machine" />
                <button @click="goBack" class="btn btn-primary back-button">
                  {{ $t("t-cards-overview") }}
                </button>
              </div>
            </div>
          </div>

          <div class="row">
            <!-- Galerie-Spalte (4/12) -->
            <div class="col-12 col-md-4 mb-4">
              <!-- Großes Hero-Bild (erstes Bild) -->
              <div
                v-if="imageUrls.length"
                class="mb-3 hero-image"
                @click="openPhotoSwipe(0)"
                style="cursor: pointer"
              >
                <img
                  :src="imageUrls[0].src"
                  alt="Hero Image"
                  class="img-fluid big-hero"
                />
              </div>
              <!-- Thumbnail-Grid: alle weiteren Bilder (exakt 5 pro Zeile) -->
              <div class="thumbnail-grid">
                <div
                  v-for="(img, i) in imageUrls.slice(1)"
                  :key="img.src"
                  class="thumb-wrapper"
                  @click="openPhotoSwipe(i + 1)"
                >
                  <img :src="img.src" alt="Thumbnail" class="thumb-img" />
                </div>
              </div>
            </div>

            <!-- Datenspalte (8/12) -->
            <div class="col-12 col-md-8">
              <!-- 1. Zeile: Fahrzeugdaten (links) & Mastdaten (rechts) -->
              <div class="row">
                <div class="col-12 col-md-6 mb-1" v-if="hasFahrzeugdaten">
                  <h3 class="sander-header">
                    {{
                      isAttachment(machine)
                        ? $t("t-detail-device-data")
                        : $t("t-detail-vehicle-data")
                    }}
                  </h3>

                  <p v-if="machine.manufacturer" class="data-row">
                    <span class="data-label"
                      >{{ $t("t-detail-manufacturer") }}:</span
                    >
                    <span class="data-value">{{ machine.manufacturer }}</span>
                  </p>
                  <p v-if="machine.model" class="data-row">
                    <span class="data-label">{{ $t("t-detail-type") }}:</span>
                    <span class="data-value">{{ machine.model }}</span>
                  </p>
                  <p v-if="machine.toc_l" class="data-row">
                    <span class="data-label"
                      >{{ $t("t-detail-construction") }}:</span
                    >
                    <span class="data-value">{{ machine.toc_l }}</span>
                  </p>
                  <p v-if="machine.enginetype_l" class="data-row">
                    <span class="data-label">{{ $t("t-detail-drive") }}:</span>
                    <span class="data-value">{{ machine.enginetype_l }}</span>
                  </p>
                  <p
                    v-if="machine.capacity && machine.capacity != 0"
                    class="data-row"
                  >
                    <span class="data-label"
                      >{{ $t("t-detail-capacity") }}:</span
                    >
                    <span class="data-value"
                      >{{ formatWithThousands(machine.capacity) }} kg</span
                    >
                  </p>
                  <p v-if="isAttachment && openWidth" class="data-row">
                    <span class="data-label">{{ $t("t-open-width") }}:</span>
                    <span class="data-value"> {{ openWidth }} mm </span>
                  </p>
                  <p
                    v-if="
                      isAttachment &&
                      machine.isoclass &&
                      machine.isoclass !== 'keine'
                    "
                    class="data-row"
                  >
                    <span class="data-label">{{ $t("t-iso-class") }}:</span>
                    <span class="data-value">{{ machine.isoclass }}</span>
                  </p>
                  <p
                    v-if="machine.loadcenter && machine.loadcenter != 0"
                    class="data-row"
                  >
                    <span class="data-label"
                      >{{ $t("t-load-centre-of-gravity") }}:</span
                    >
                    <span class="data-value">{{ machine.loadcenter }} mm</span>
                  </p>
                  <p v-if="machine.yoc && machine.yoc != 0" class="data-row">
                    <span class="data-label">{{ $t("t-detail-year") }}:</span>
                    <span class="data-value">{{ machine.yoc }}</span>
                  </p>
                  <p
                    v-if="machine.hours && machine.hours != 0"
                    class="data-row"
                  >
                    <span class="data-label">{{ $t("t-detail-hours") }}:</span>
                    <span class="data-value">{{ machine.hours }}</span>
                  </p>
                  <p
                    v-if="machine.weight && machine.weight != 0"
                    class="data-row"
                  >
                    <span class="data-label">{{ $t("t-detail-weight") }}:</span>
                    <span class="data-value"
                      >{{ formatWithThousands(machine.weight) }} kg</span
                    >
                  </p>
                  <p v-if="machine.internalno" class="data-row">
                    <span class="data-label"
                      >{{ $t("t-detail-internalno") }}:</span
                    >
                    <span class="data-value">{{ machine.internalno }}</span>
                  </p>
                </div>
                <div class="col-12 col-md-6 mb-1">
                  <template v-if="!isAttachment(machine)">
                    <h3 class="sander-header">
                      {{ $t("t-detail-mast-data") }}
                    </h3>

                    <!-- Masttyp -->
                    <p v-if="machine.masttype_l" class="data-row">
                      <span class="data-label"
                        >{{ $t("t-detail-masttype") }}:</span
                      >
                      <span class="data-value">{{ machine.masttype_l }}</span>
                    </p>

                    <!-- Hubhöhe -->
                    <p
                      v-if="machine.liftingheight && machine.liftingheight != 0"
                      class="data-row"
                    >
                      <span class="data-label"
                        >{{ $t("t-detail-liftingheight") }}:</span
                      >
                      <span class="data-value"
                        >{{
                          formatWithThousands(machine.liftingheight)
                        }}
                        mm</span
                      >
                    </p>

                    <!-- Geschlossene Bauhöhe -->
                    <p
                      v-if="machine.closedheight && machine.closedheight != 0"
                      class="data-row"
                    >
                      <span class="data-label"
                        >{{ $t("t-detail-closedheight") }}:</span
                      >
                      <span class="data-value"
                        >{{
                          formatWithThousands(machine.closedheight)
                        }}
                        mm</span
                      >
                    </p>

                    <!-- Freihub -->
                    <p
                      v-if="machine.freelift && machine.freelift != 0"
                      class="data-row"
                    >
                      <span class="data-label"
                        >{{ $t("t-detail-freelift") }}:</span
                      >
                      <span class="data-value"
                        >{{ formatWithThousands(machine.freelift) }} mm</span
                      >
                    </p>

                    <!-- Forks -->
                    <p v-if="forksFormatted" class="data-row">
                      <span class="data-label"
                        >{{ $t("t-detail-forklength") }}:</span
                      >
                      <span class="data-value"
                        >{{ forksFormatted.forks_1 }} mm</span
                      >
                    </p>
                    <p v-if="forksFormatted" class="data-row">
                      <span class="data-label"
                        >{{ $t("t-detail-forkwidth") }}:</span
                      >
                      <span class="data-value"
                        >{{ forksFormatted.forks_2 }} mm</span
                      >
                    </p>
                    <p v-if="forksFormatted" class="data-row">
                      <span class="data-label"
                        >{{ $t("t-detail-forkthickness") }}:</span
                      >
                      <span class="data-value"
                        >{{ forksFormatted.forks_3 }} mm</span
                      >
                    </p>

                    <p v-if="machine.forkcarr" class="data-row">
                      <span class="data-label"
                        >{{ $t("t-detail-forkcarr") }}:</span
                      >
                      <span class="data-value">{{ machine.forkcarr }}</span>
                    </p>

                    <!-- Batterie (falls vorhanden) -->
                    <div v-if="machine.battery">
                      <h3 class="sander-header">
                        {{ $t("t-detail-engine-battery-charger") }}
                      </h3>
                      <p class="data-row">
                        <span class="data-label"
                          >{{ $t("t-detail-battery") }}:</span
                        >
                        <span class="data-value">{{ machine.battery }}</span>
                      </p>
                    </div>
                  </template>

                  <!-- B) Dimensions-Block, wenn Attachment -->
                  <template v-else>
                    <h3 class="sander-header">
                      {{ $t("t-detail-dimensions") }}
                    </h3>

                    <!-- t-detail-back-broad = carrbroad -->
                    <p
                      v-if="machine.carrbroad && machine.carrbroad != 0"
                      class="data-row"
                    >
                      <span class="data-label"
                        >{{ $t("t-detail-back-broad") }}:</span
                      >
                      <span class="data-value">{{ machine.carrbroad }} mm</span>
                    </p>

                    <!-- t-detail-balancepoint = balancepoint -->
                    <p
                      v-if="machine.balancepoint && machine.balancepoint != 0"
                      class="data-row"
                    >
                      <span class="data-label"
                        >{{ $t("t-detail-balancepoint") }}:</span
                      >
                      <span class="data-value"
                        >{{ machine.balancepoint }} mm</span
                      >
                    </p>

                    <!-- t-detail-frontend-dimension = frontend -->
                    <p
                      v-if="machine.frontend && machine.frontend != 0"
                      class="data-row"
                    >
                      <span class="data-label"
                        >{{ $t("t-detail-frontend-dimension") }}:</span
                      >
                      <span class="data-value">{{ machine.frontend }} mm</span>
                    </p>

                    <!-- t-detail-armlength = armlength -->
                    <p
                      v-if="machine.armlength && machine.armlength != 0"
                      class="data-row"
                    >
                      <span class="data-label"
                        >{{ $t("t-detail-armlength") }}:</span
                      >
                      <span class="data-value">{{ machine.armlength }} mm</span>
                    </p>

                    <!-- t-detail-armbroade = armbroad -->
                    <p
                      v-if="machine.armbroad && machine.armbroad != 0"
                      class="data-row"
                    >
                      <span class="data-label"
                        >{{ $t("t-detail-armbroade") }}:</span
                      >
                      <span class="data-value">{{ machine.armbroad }} mm</span>
                    </p>

                    <!-- t-detail-armsize = armsize -->
                    <p
                      v-if="machine.armsize && machine.armsize != 0"
                      class="data-row"
                    >
                      <span class="data-label"
                        >{{ $t("t-detail-armsize") }}:</span
                      >
                      <span class="data-value">{{ machine.armsize }} mm</span>
                    </p>
                  </template>
                </div>
              </div>
              <!-- 2. Zeile: Beschreibung (links) & Sonderausstattung (rechts) -->
              <div class="row">
                <div class="col-12 col-md-6 mb-1" v-if="hasBeschreibung">
                  <h3 class="sander-header">
                    {{ $t("t-detail-description") }}
                  </h3>
                  <p
                    v-if="
                      machine.tyres_l &&
                      machine.tyres_l !== 'Unknown' &&
                      machine.tyres_l !== 'Unbekannt'
                    "
                    class="data-row"
                  >
                    <span class="data-label"
                      >{{ $t("t-detail-tyresfront-lbl") }}:</span
                    >
                    <span class="data-value">{{ machine.tyres_l }}</span>
                  </p>
                  <p
                    v-if="machine.tyresfront && machine.tyresfront != 0"
                    class="data-row"
                  >
                    <span class="data-label"
                      >{{ $t("t-detail-tyresfront-size") }}:</span
                    >
                    <span class="data-value">{{ machine.tyresfront }}</span>
                  </p>
                  <p
                    v-if="
                      machine.tyres_l &&
                      machine.tyres_l !== 'Unknown' &&
                      machine.tyres_l !== 'Unbekannt'
                    "
                    class="data-row"
                  >
                    <span class="data-label"
                      >{{ $t("t-detail-tyresrear-lbl") }}:</span
                    >
                    <span class="data-value">{{ machine.tyres_l }}</span>
                  </p>
                  <p
                    v-if="machine.tyresrear && machine.tyresrear != 0"
                    class="data-row"
                  >
                    <span class="data-label"
                      >{{ $t("t-detail-tyresrear-size") }}:</span
                    >
                    <span class="data-value">{{ machine.tyresrear }}</span>
                  </p>
                  <p v-if="machine.techcond_l" class="data-row">
                    <span class="data-label"
                      >{{ $t("t-detail-techcond") }}:</span
                    >
                    <span class="data-value">{{ machine.techcond_l }}</span>
                  </p>
                  <p v-if="machine.optcond_l" class="data-row">
                    <span class="data-label"
                      >{{ $t("t-detail-optcond") }}:</span
                    >
                    <span class="data-value">{{ machine.optcond_l }}</span>
                  </p>
                </div>
                <div class="col-12 col-md-6 mb-1" v-if="hasSonderausstattung">
                  <h3 class="sander-header">{{ $t("t-detail-extras") }}</h3>
                  <div v-if="machine.attachbit_l && machine.attachbit_l.length">
                    <p class="data-row">
                      <span class="data-label"
                        >{{ $t("t-detail-attachments") }}:</span
                      >
                    </p>
                    <ul class="">
                      <li v-for="(item, idx) in machine.attachbit_l" :key="idx">
                        {{ item }}
                      </li>
                    </ul>
                  </div>
                  <div v-if="formattedAddeqbits.length">
                    <p class="data-row">
                      <span class="data-label"
                        >{{ $t("t-detail-other-equipment") }}:</span
                      >
                    </p>
                    <ul class="">
                      <li
                        v-for="(item, index) in formattedAddeqbits"
                        :key="index"
                      >
                        {{ item }}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- NEUE SEKTION: Weitere Bemerkung -->
              <div v-if="formattedBemerkung.length" class="bemerkung-container">
                <h3 class="sander-header">{{ $t("t-detail-comments") }}</h3>
                <div class="bemerkung-grid">
                  <!-- bemerkungChunks enthält Spalten (je 3 Elemente) -->
                  <div
                    v-for="(column, colIndex) in bemerkungChunks"
                    :key="colIndex"
                    class="bemerkung-column"
                  >
                    <ul>
                      <li v-for="(item, idx) in column" :key="idx">
                        {{ item }}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- 3. Zeile: Ansprechpartner & Preis -->
              <div class="row">
                <div class="col-12 col-md-6 mb-4 order-2 order-md-1">
                  <h3 class="sander-header">{{ $t("t-detail-contact") }}</h3>
                  <div>
                    <img
                      src="@/assets/images/persons/ansprechpartner_1.jpg"
                      alt="Thomas Krumpöck"
                      class="img-fluid w-100"
                    />
                  </div>
                  <p class="data-row">
                    <span class="data-label fw-bold">Thomas Krumpöck</span>
                  </p>
                  <p class="data-row">
                    <span class="data-value">{{
                      $t("t-detail-contact-title")
                    }}</span>
                  </p>
                  <p class="data-row">
                    <span class="data-label">{{
                      $t("t-detail-contact-phone")
                    }}</span>
                    <span class="data-value"
                      ><a href="tel:+4937152338486"
                        >+49 (0)371 52338-486</a
                      ></span
                    >
                  </p>
                  <p class="data-row">
                    <span class="data-label">{{
                      $t("t-detail-contact-mobile")
                    }}</span>
                    <span class="data-value"
                      ><a href="tel:+4915154433809"
                        >+49 (0)151 54433809</a
                      ></span
                    >
                  </p>
                  <p class="data-row">
                    <span class="data-label">{{
                      $t("t-detail-contact-email")
                    }}</span>
                    <span class="data-value"
                      ><a
                        :href="`mailto:gebrauchtstapler@sander-ft.de?subject=${encodeURIComponent(
                          $t('mail.subject', {
                            manufacturer: machine.manufacturer,
                            model: machine.model,
                            internalno: machine.internalno,
                          })
                        )}&body=${encodeURIComponent(
                          $t('mail.body', {
                            manufacturer: machine.manufacturer,
                            model: machine.model,
                            internalno: machine.internalno,
                          })
                        )}`"
                      >
                        gebrauchtstapler@sander-ft.de
                      </a></span
                    >
                  </p>
                </div>
                <div class="col-12 col-md-6 mb-4 order-1 order-md-2">
                  <h3 class="sander-header">{{ $t("t-detail-price") }}</h3>
                  <p class="data-value price-wrapper">
                    <span class="price"
                      >{{ formatWithThousands(machine.custprice) }} EUR</span
                    >
                    <span class="mwst">({{ $t("t-detail-taxes") }})</span>
                  </p>
                  <p class="data-value">
                    <span class="data-value">{{
                      $t("t-detail-subject-to-prior-sale")
                    }}</span>
                  </p>
                  <div class="data-value">
                    <a
                      :href="`mailto:gebrauchtstapler@sander-ft.de?subject=${encodeURIComponent(
                        $t('mail.subject', {
                          manufacturer: machine.manufacturer,
                          model: machine.model,
                          internalno: machine.internalno,
                        })
                      )}&body=${encodeURIComponent(
                        $t('mail.body', {
                          manufacturer: machine.manufacturer,
                          model: machine.model,
                          internalno: machine.internalno,
                        })
                      )}`"
                    >
                      <button class="btn btn-primary back-button">
                        {{ $t("t-details-callto") }}
                      </button>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="action-container-bottom">
            <PdfDownloadButton :machine="machine" />
            <button @click="goBack" class="btn btn-primary back-button">
              {{ $t("t-cards-overview") }}
            </button>
          </div>
          <div class="machine-updated">
            <small
              >{{ $t("t-last-updated") }}:
              {{ formatDate(machine.updated_at) }}</small
            >
          </div>
        </div>
        <!-- Falls Maschine = null -->
        <div v-else class="loading">
          {{ $t("loadingDetails") }}
        </div>
      </div>
    </section>

    <!-- NEUER ABSCHNITT: ÄHNLICHE MASCHINEN -->
    <section v-if="similarMachines.length" class="similar-machines-section">
      <div class="container">
        <h3 class="sander-header mb-3">
          {{ $t("t-detail-similar-machines") }}
        </h3>
        <!-- Einfache horizontale Scroll-Liste -->
        <div class="similar-machines-slider">
          <div
            v-for="m in similarMachines"
            :key="m.internalno"
            class="machine-card"
          >
            <!-- Struktur wie in der Cards-Ansicht -->
            <div class="image-container">
              <router-link
                :to="{
                  name: 'MachineDetails',
                  params: { internalno: m.internalno },
                }"
                class="image-link"
              >
                <img
                  :src="getCardImageUrl(m.internalno)"
                  @error="handleImageError"
                  :alt="m.manufacturer + ' ' + m.model"
                  class="machine-image"
                  loading="lazy"
                />

                <div class="overlay">
                  <span class="overlay-text">
                    {{ $t("t-cards-viewDetails") }}
                  </span>
                </div>
              </router-link>
            </div>
            <div class="price-bar">
              {{ formatWithThousands(m.custprice) }} EUR
            </div>
            <div class="machine-info">
              <h3 class="manufacturer">{{ m.manufacturer }}</h3>
              <p class="model text-dark">{{ m.model }}</p>
              <div class="machine-features">
                <div class="feature">
                  <span class="label">
                    {{ $t("t-cards-type-of-construction") }}:
                  </span>
                  <span class="value text-primary">{{ m.toc_l }}</span>
                </div>
                <div class="feature">
                  <span class="label"> {{ $t("t-cards-capacity") }}: </span>
                  <span class="value text-primary">
                    {{ formatValue(m.capacity, "capacity") }}
                  </span>
                </div>
                <div class="feature">
                  <span class="label">
                    {{
                      isAttachment(m)
                        ? $t("t-iso-class")
                        : $t("t-cards-liftingHeight")
                    }}:
                  </span>
                  <span class="value text-primary">
                    {{
                      isAttachment(m)
                        ? m.isoclass && m.isoclass !== "keine"
                          ? m.isoclass
                          : "-"
                        : m.liftingheight && m.liftingheight !== 0
                        ? m.liftingheight + " mm"
                        : "-"
                    }}
                  </span>
                </div>
                <div class="feature">
                  <span class="label">
                    {{ $t("t-cards-yearOfConstruction") }}:
                  </span>
                  <span class="value text-primary">
                    {{ formatValue(m.yoc, "yoc") }}
                  </span>
                </div>
                <div class="feature">
                  <span class="label">
                    {{ $t("t-cards-device-number") }}:
                  </span>
                  <span class="value text-primary">
                    {{ m.internalno }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FOOTER -->
    <FooterSection />
  </div>
</template>

<script>
import { ref, onMounted, watch, onBeforeUnmount, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { storeToRefs } from "pinia";
import { useMachineStore } from "@/stores/useMachineStore";
import { useAuthUserStore } from "@/stores/useAuthUserStore";
import i18n from "@/i18n";

import appConfig from "../../../app.config";

import { useHead } from "@vueuse/head";
import { onBeforeRouteLeave } from "vue-router";

import HeaderMenu from "@/components/modules/HeaderMenu.vue";
import FooterSection from "../../components/modules/FooterSection.vue";
import PdfDownloadButton from "@/components/modules/PdfDownloadButton.vue";

// PhotoSwipe v5
import PhotoSwipeLightbox from "photoswipe/lightbox";
import "photoswipe/style.css";

import { onBeforeRouteUpdate } from "vue-router";

export default {
  name: "MachineDetails",
  components: {
    HeaderMenu,
    FooterSection,
    PdfDownloadButton,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const internalno = ref(route.params.internalno);

    // Pinia-Stores
    const machineStore = useMachineStore();
    const authUserStore = useAuthUserStore();
    const { machines, loading } = storeToRefs(machineStore);

    // Reaktive Zustände
    const machine = ref(null);
    const localError = ref(null);
    const imageUrls = ref([]);
    let lightbox = null;

    // Computed-Werte für Meta-Daten
    const dynamicTitle = computed(() =>
      machine.value
        ? `${machine.value.manufacturer} ${machine.value.model} | ${appConfig.title}`
        : "Maschinendetails"
    );
    const dynamicDescription = computed(() =>
      machine.value
        ? `Details zum ${machine.value.manufacturer} ${machine.value.model} – jetzt mehr erfahren!`
        : appConfig.description
    );
    const dynamicKeywords = computed(() =>
      machine.value
        ? `${appConfig.keywords}, ${machine.value.manufacturer}, ${machine.value.model}, ${machine.value.internalno}, ${machine.value.toc_l}`
        : appConfig.keywords
    );

    // Konsolidierter useHead-Aufruf, der die computed-Werte nutzt
    const headInstance = useHead({
      title: dynamicTitle,
      meta: [
        { name: "description", content: dynamicDescription },
        { name: "keywords", content: dynamicKeywords },
      ],
    });

    onBeforeRouteLeave((to, from, next) => {
      headInstance.dispose();
      next();
    });

    // Reagiere auf Routenwechsel,
    // ohne die Komponente neu zu unmounten
    onBeforeRouteUpdate(async (to, from, next) => {
      if (to.params.internalno !== from.params.internalno) {
        // Hier neu laden
        internalno.value = to.params.internalno;

        await loadMachine();
        imageUrls.value = []; // explizit leeren
        await loadImages();
        machine.value.imageUrls = [...imageUrls.value];
        // Navigation fortsetzen
        next();
      } else {
        next();
      }
    });

    const formatDate = (dateString) => {
      if (!dateString) return "";
      const d = new Date(dateString);
      const day = d.getDate().toString().padStart(2, "0");
      const month = (d.getMonth() + 1).toString().padStart(2, "0");
      const year = d.getFullYear();
      return `${day}.${month}.${year}`;
    };

    const getImageSize = (url) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          resolve({ width: img.naturalWidth, height: img.naturalHeight });
        };
        img.onerror = () => {
          resolve({ width: 800, height: 600 });
        };
        img.src = url;
      });
    };

    // ----------------------------------
    // 1) Maschine laden
    // ----------------------------------
    const loadImages = async () => {
      const internalNo = machine.value.internalno;

      //console.log("Loading images for", internalNo);
      imageUrls.value = [];
      for (let i = 1; i <= 20; i++) {
        const url = `${process.env.VUE_APP_API_URL}/images/${internalNo}-${i}.jpg`;
        try {
          const response = await fetch(url, { method: "HEAD" });
          if (response.ok) {
            const size = await getImageSize(url);

            imageUrls.value.push({
              src: url,
              width: size.width,
              height: size.height,
            });
            //imageUrls.value.push(url);
          } else {
            break;
          }
        } catch (error) {
          break;
        }
      }
      machine.value.imageUrls = [...imageUrls.value];
    };

    const loadMachine = async () => {
      try {
        const defaultLocale =
          authUserStore.language || process.env.VUE_APP_I18N_LOCALE || "en";
        if (i18n.global.locale !== defaultLocale) {
          i18n.global.locale = defaultLocale;
        }

        await machineStore.fetchMachines(defaultLocale);

        machine.value = machines.value.find(
          (m) => m.internalno === internalno.value
        );

        if (!machine.value) {
          localError.value = i18n.global.t("t-detail-machine-not-found");
          return;
        }
      } catch (err) {
        console.error(i18n.global.t("t-detail-machine-load-error-colon"), err);
        localError.value = i18n.global.t("t-detail-machine-load-error-dot");
      }
    };

    // ----------------------------------
    // 2) PhotoSwipe
    // ----------------------------------
    const openPhotoSwipe = (index = 0) => {
      if (lightbox) {
        lightbox.destroy();
        lightbox = null;
      }
      const items = imageUrls.value.map((imgObj) => ({
        src: imgObj.src,
        // Dummywerte für Hochkantbilder – hier 800 x 1200
        width: imgObj.width,
        height: imgObj.height,
      }));
      lightbox = new PhotoSwipeLightbox({
        dataSource: items,
        pswpModule: () => import("photoswipe"),
        initialSlide: index,
        showHideAnimationType: "zoom",
        zoom: true,
        getDoubleTapZoom: () => 3,
        wheelToZoom: true,
        padding: { top: 20, bottom: 20 },
        initialZoomLevel: "fit",
        secondaryZoomLevel: 1.5,
        maxZoomLevel: 2,
      });
      lightbox.init();
      lightbox.loadAndOpen(index);
    };

    // ----------------------------------
    // 3) Formatierungen
    // ----------------------------------
    const formatWithThousands = (value) => {
      if (!value || value === 0) return "-";
      return new Intl.NumberFormat("de-DE").format(value);
    };

    // --- NEU: Prüfen, ob Maschine Anbaugerät/Attachment ist ---
    function isAttachment(machine) {
      if (!machine || machine.machine_typ == null) return false;
      return machine.machine_typ === 2; // Typ=2 = Attachment
    }

    // --- NEU: Offene Weite (rangefrom / rangeto) ---
    const openWidth = computed(() => {
      if (!machine.value) return "";
      const fromVal = Number(machine.value.rangefrom) || 0;
      const toVal = Number(machine.value.rangeto) || 0;
      // Beide = 0 => leer zurück, Zeile wird ausgeblendet
      if (fromVal === 0 && toVal === 0) return "";
      // Wenn toVal=0 => nur fromVal
      if (toVal === 0) return String(fromVal);
      // Sonst "123 - 456"
      return `${fromVal} - ${toVal}`;
    });

    const formatValue = (value, type) => {
      if (!value || value === 0) return "-";
      switch (type) {
        case "capacity":
          return `${value} kg`;
        case "liftingheight":
          return `${value} mm`;
        default:
          return value;
      }
    };

    // Gabeln (forks) formatieren
    const forksFormatted = computed(() => {
      if (!machine.value || !machine.value.forks) return null;
      const forksStr = machine.value.forks;
      const match = forksStr.match(
        /([\d.,]+)\s*x\s*([\d.,]+)\s*x\s*([\d.,]+)/i
      );
      if (!match) return null;
      const formatFork = (s) => {
        let num = parseFloat(s.replace(/,/g, "."));
        if (isNaN(num)) return s;
        return new Intl.NumberFormat("de-DE").format(num);
      };
      return {
        forks_1: formatFork(match[1]),
        forks_2: formatFork(match[2]),
        forks_3: formatFork(match[3]),
      };
    });

    // addeqbits_l als Liste
    const formattedAddeqbits = computed(() => {
      if (!machine.value || !machine.value.addeqbits_l) return [];
      let s = machine.value.addeqbits_l;
      if (Array.isArray(s)) {
        return s.map((item) => item.trim()).filter((item) => item.length);
      }
      if (typeof s !== "string") {
        s = String(s);
      }
      s = s.replace(/[{}]/g, "");
      return s
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item.length);
    });

    // Bemerkungen (addequip)
    const formattedBemerkung = computed(() => {
      if (!machine.value || !machine.value.addequip) return [];
      // <br> -> Komma
      let text = machine.value.addequip.replace(/<br\s*\/?>/gi, ",");
      // Split an Komma
      let items = text.split(",").map((el) => el.trim());
      // Leere entfernen
      items = items.filter((el) => el.length > 0);
      return items;
    });

    const bemerkungChunks = computed(() => {
      const all = formattedBemerkung.value;
      const chunks = [];
      for (let i = 0; i < all.length; i += 3) {
        chunks.push(all.slice(i, i + 3));
      }
      return chunks;
    });

    // ----------------------------------
    // 4) Filter-Checks
    // ----------------------------------
    const hasFahrzeugdaten = computed(() => {
      if (!machine.value) return false;
      const m = machine.value;
      return (
        m.manufacturer ||
        m.model ||
        m.toc_l ||
        m.enginetype_l ||
        (m.capacity && m.capacity != 0) ||
        (m.yoc && m.yoc != 0) ||
        (m.hours && m.hours != 0) ||
        (m.weight && m.weight != 0) ||
        m.internalno
      );
    });

    const hasMastdaten = computed(() => {
      if (!machine.value) return false;
      const m = machine.value;
      return (
        m.masttype_l ||
        (m.liftingheight && m.liftingheight != 0) ||
        (m.closedheight && m.closedheight != 0) ||
        (m.freelift && m.freelift != 0) ||
        (m.forks && m.forks.trim() !== "") ||
        (m.forkcarr && m.forkcarr != 0) ||
        m.battery
      );
    });

    const hasBeschreibung = computed(() => {
      if (!machine.value) return false;
      const m = machine.value;
      const tyresValid =
        m.tyres_l && m.tyres_l !== "Unknown" && m.tyres_l !== "Unbekannt";
      return (
        tyresValid || m.tyresfront || m.tyresrear || m.techcond_l || m.optcond_l
      );
    });

    const hasSonderausstattung = computed(() => {
      if (!machine.value) return false;
      const m = machine.value;
      const attachArray = Array.isArray(m.attachbit_l)
        ? m.attachbit_l
        : m.attachbit_l
        ? [m.attachbit_l]
        : [];
      const addeqArray = formattedAddeqbits.value;
      return attachArray.length > 0 || addeqArray.length > 0;
    });

    const goBack = () => {
      router.push({ path: "/products" });
    };

    // ----------------------------------
    // 5) ÄHNLICHE MASCHINEN
    // ----------------------------------
    /**
     * Holt ähnlichste Maschinen nach deiner 4-Prioritäten-Logik:
     * 1) capacity -> exakte Übereinstimmung, sonst +/- 500 bzw. +1500
     * 2) liftingheight -> exakte Übereinstimmung, sonst -500
     * 3) enginetype_l -> immer identisch
     * 4) manufacturer -> immer identisch
     */
    const similarMachines = computed(() => {
      if (!machine.value) return [];

      // Referenz-Preis
      const basePrice = Number(machine.value.custprice) || 0;

      // Alle außer das aktuelle Gerät
      const allOthers = machines.value.filter(
        (m) => m.internalno !== machine.value.internalno
      );

      // Ergebnisliste
      const results = [];
      // Set zum Verhindern von Dubletten
      const used = new Set();

      // Hilfsfunktion: Fügt bis zu 6 Einträge hinzu
      function addUpToSixFrom(list) {
        for (const item of list) {
          if (results.length >= 6) break;
          if (!used.has(item.internalno)) {
            used.add(item.internalno);
            results.push(item);
          }
        }
      }

      function getRemainingCandidates() {
        return allOthers.filter((m) => !used.has(m.internalno));
      }

      // Hilfsfunktion: Sortierung einer Liste nach Preis-Differenz zum basePrice
      // Kleinstes Delta => zuerst
      function sortByPriceCloseness(array) {
        return array.slice().sort((a, b) => {
          const diffA = Math.abs(Number(a.custprice) - basePrice);
          const diffB = Math.abs(Number(b.custprice) - basePrice);
          return diffA - diffB;
        });
      }

      // OPTIONAL: Filter, um völlig abweichende Preise komplett auszuschließen
      // Beispiel: nur in [50%, 200%] des basePrice
      function filterByPriceRange(array) {
        if (basePrice === 0) return array; // wenn dein Gerät 0 € kostet (?), nicht filtern
        const minPrice = basePrice * 0.5; // halber Preis
        const maxPrice = basePrice * 2.0; // doppelter Preis
        return array.filter((m) => {
          const p = Number(m.custprice) || 0;
          return p >= minPrice && p <= maxPrice;
        });
      }

      // ===============================================================
      // SCHRITT 1: Kapazität
      // ===============================================================
      const cap = Number(machine.value.capacity) || 0;
      if (cap > 0 && results.length < 6) {
        // 1a) exakt
        let capExact = getRemainingCandidates().filter(
          (m) => Number(m.capacity) === cap
        );

        // Sortiere capExact nach preislicher Nähe
        capExact = sortByPriceCloseness(capExact);

        // OPTIONAL: zusätzlich filtern, wenn du "viel zu teure" oder "viel zu billige" rauswerfen willst
        capExact = filterByPriceRange(capExact);

        if (capExact.length > 0) {
          addUpToSixFrom(capExact);
        } else {
          // 1b) Range [cap-500, cap+1500]
          const minCap = Math.max(cap - 500, 0);
          const maxCap = cap + 1500;
          let capRange = getRemainingCandidates().filter((m) => {
            const mc = Number(m.capacity) || 0;
            return mc >= minCap && mc <= maxCap;
          });
          // Sortieren + filtern
          capRange = sortByPriceCloseness(capRange);
          capRange = filterByPriceRange(capRange);

          if (capRange.length > 0) {
            addUpToSixFrom(capRange);
          }
        }
      }

      // ===============================================================
      // SCHRITT 2: Hubhöhe
      // ===============================================================
      const lift = Number(machine.value.liftingheight) || 0;
      if (lift > 0 && results.length < 6) {
        // 2a) exakt
        let liftExact = getRemainingCandidates().filter(
          (m) => Number(m.liftingheight) === lift
        );
        liftExact = sortByPriceCloseness(liftExact);
        liftExact = filterByPriceRange(liftExact);

        if (liftExact.length > 0) {
          addUpToSixFrom(liftExact);
        } else {
          // 2b) >= lift-500
          const minLift = Math.max(lift - 500, 0);
          let liftRange = getRemainingCandidates().filter((m) => {
            const ml = Number(m.liftingheight) || 0;
            return ml >= minLift;
          });
          liftRange = sortByPriceCloseness(liftRange);
          liftRange = filterByPriceRange(liftRange);

          if (liftRange.length > 0) {
            addUpToSixFrom(liftRange);
          }
        }
      }

      // ===============================================================
      // SCHRITT 3: Antriebsart
      // ===============================================================
      if (machine.value.enginetype_l && results.length < 6) {
        let sameEngine = getRemainingCandidates().filter(
          (m) => m.enginetype_l === machine.value.enginetype_l
        );
        sameEngine = sortByPriceCloseness(sameEngine);
        sameEngine = filterByPriceRange(sameEngine);

        if (sameEngine.length > 0) {
          addUpToSixFrom(sameEngine);
        }
      }

      // ===============================================================
      // SCHRITT 4: Hersteller (Fallback)
      // ===============================================================
      if (machine.value.manufacturer && results.length < 6) {
        let sameManu = getRemainingCandidates().filter(
          (m) => m.manufacturer === machine.value.manufacturer
        );
        sameManu = sortByPriceCloseness(sameManu);
        sameManu = filterByPriceRange(sameManu);

        if (sameManu.length > 0) {
          addUpToSixFrom(sameManu);
        }
      }

      return results;
    });

    // Hilfsfunktionen fürs Card-Layout
    const getCardImageUrl = (internalNo) => {
      // Für das Card-Bild reicht das erste Bild
      return `${process.env.VUE_APP_API_URL}/images/${internalNo}-1.jpg`;
    };
    const handleImageError = (event) => {
      event.target.src = "https://via.placeholder.com/150";
    };

    watch(
      () => authUserStore.language,
      async (newLang, oldLang) => {
        if (newLang !== oldLang) {
          await loadMachine();
          imageUrls.value = []; // Leeren, um alte Daten zu entfernen
          await loadImages();
          // machine.imageUrls wird in loadImages gesetzt, falls nötig:
          machine.value.imageUrls = [...imageUrls.value];
        }
      }
    );
    /*
    watch(
      () => route.params.internalno,
      async (newNo, oldNo) => {
        if (newNo && newNo !== oldNo) {
          await loadMachine();
        }
      }
    );
    */
    // ----------------------------------
    // 6) Lifecycle
    // ----------------------------------

    watch(
      () => authUserStore.language,
      async (newLang, oldLang) => {
        // Nur dann neu laden, wenn sich die Sprache wirklich ändert
        if (newLang !== oldLang) {
          oldLang = newLang;
          console.log("Sprache hat sich geändert:", newLang);
          await loadMachine();
        }
      }
    );

    onMounted(async () => {
      await loadMachine();
      await loadImages();
    });

    onBeforeUnmount(() => {
      if (lightbox) {
        lightbox.destroy();
        lightbox = null;
      }
    });

    // ----------------------------------
    // Return an Template
    // ----------------------------------
    return {
      machine,
      error: localError,
      loading,
      imageUrls,
      formatDate,
      // PhotoSwipe
      openPhotoSwipe,

      // Format
      formatWithThousands,
      isAttachment,
      openWidth,
      formatValue,
      forksFormatted,
      formattedAddeqbits,
      formattedBemerkung,
      bemerkungChunks,

      // Checks
      hasFahrzeugdaten,
      hasMastdaten,
      hasBeschreibung,
      hasSonderausstattung,

      // Ähnliche Maschinen
      similarMachines,
      getCardImageUrl,
      handleImageError,

      // Allg
      goBack,
    };
  },
};
</script>

<style scoped>
.error-image {
  height: 60px;
  opacity: 0.3;
}

.action-container-top {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.action-container-bottom {
  display: none;
  gap: 10px;
  justify-content: center;
}

.machine-details-container {
  background-color: #fff;
  border-radius: 8px;
  padding: 1rem;
}

.machine-updated {
  text-align: right;
  display: block; /* oder block */
  padding-top: 25px;
  font-size: 0.9rem;
  color: #777;
}

/* Großes Hero-Bild */
.big-hero {
  width: 100%;
  height: auto;
  object-fit: contain;
  max-height: 800px;
  display: block;
  cursor: pointer;
}

/* Thumbnail-Grid */

.thumbnail-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
}

.thumb-wrapper {
  flex: 0 0 20%;
  cursor: pointer;
}
.thumb-img {
  width: 100%;
  height: auto;
  object-fit: contain;
  display: block;
  max-height: 120px;
}

/* Data Rows */
.data-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
  margin: 0.3rem 0;
}
.data-label {
  text-align: left;
  font-weight: 700;
}
.data-value {
  text-align: right;
  color: #555;
}

/* h3 Headings */
.machine-details-container h3 {
  margin-bottom: 0.5rem;
  font-size: 1rem;
  border-bottom: 1px solid #ddd;
  padding-bottom: 0.3rem;
  margin-top: 1rem;
  text-align: left;
}

/* Loading / Error */
.loading {
  text-align: center;
  font-size: 1.2em;
  color: #555;
}
.error {
  text-align: center;
  font-size: 1.2em;
  color: var(--primary) !important;
}

/* Style-Klassen */
.sander-header {
  color: var(--primary) !important;
}
.price {
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--primary) !important;
}
.mwst {
  font-size: 0.8rem;
  padding-left: 5px;
  font-weight: 400;
  color: grey;
}

/* Bemerkung: 3er-Chunks */

.bemerkung-grid {
  display: flex;
  flex-wrap: wrap; /* Erlaubt das Umbrechen der Spalten */
  gap: 1rem; /* Optional: Abstand zwischen den Spalten */
}

/* Für größere Bildschirme: Spalten nebeneinander */
.bemerkung-column {
  flex: 0 0 auto;
  max-width: 33.33%;
}
.bemerkung-column ul {
  list-style: disc outside;
  margin: 0;
  padding: 0;
}

.bemerkung-column li {
  margin-left: 1rem;
  max-width: 250px; /* Maximale Breite, nach Bedarf anpassen */
  overflow-wrap: break-word; /* sorgt dafür, dass lange Wörter umgebrochen werden */
  word-wrap: break-word; /* für ältere Browser */
}

/* Ähnliche Maschinen: Slider */
.similar-machines-section {
  margin-top: 2rem;
  margin-bottom: 2rem;
}
.similar-machines-slider {
  display: flex;
  overflow-x: auto;
  gap: 16px;
  padding-bottom: 10px;
}
.similar-machines-slider::-webkit-scrollbar {
  height: 8px;
}
.similar-machines-slider::-webkit-scrollbar-thumb {
  background-color: #ccc;
  border-radius: 4px;
}
.machine-card {
  flex: 0 0 220px; /* Feste Breite wie im Grid */
  display: flex;
  flex-direction: column;
  height: 100%;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  background-color: #fff;
  min-height: 360px; /* damit die Box nicht zu klein wird */
}
.image-container {
  position: relative;
  overflow: hidden;
  height: 250px;
}
.price-bar {
  position: static;
  margin-top: 0px;
  width: 100%;
  background-color: var(--dark);
  color: #fff !important;
  padding: 5px 10px;
  box-sizing: border-box;
  text-align: right;
  font-weight: bold;
  font-size: 0.9em;
}
.image-link {
  display: block;
  width: 100%;
  height: 100%;
  text-decoration: none;
}
.machine-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.3s ease;
}
.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}
.image-container:hover .overlay {
  opacity: 1;
}
.overlay-text {
  color: #fff;
  font-size: 1.2em;
  font-weight: bold;
  text-align: center;
  pointer-events: none;
}
.image-container:hover .machine-image {
  transform: scale(1.05);
}

.machine-info {
  padding: 12px;
}
.manufacturer {
  margin: 8px 0 0px;
  font-size: 1.1em;
  color: #333;
}
.model {
  margin: 0;
  font-size: 0.9em;
  font-weight: 400;
}
.machine-features {
  margin-top: 8px;
}
.feature {
  display: flex;
  justify-content: space-between;
  font-size: 0.95em;
  color: #555;
  margin-bottom: 4px;
  text-align: right;
}
.label {
  color: #999;
  text-align: left;
}

/* PhotoSwipe-Fix */
.pswp__img {
  max-height: none !important;
  height: auto !important;
  object-fit: contain;
}

.price-wrapper {
  margin-bottom: 0px;
}

#machine-section {
  padding-top: 130px;
}

/* Responsive */
@media (max-width: 768px) {
  .machine-header {
    font-size: 1.6em;
  }
  .action-container-top {
    display: none;
  }
  .action-container-bottom {
    display: flex;
  }
  .bemerkung-column {
    flex: 0 0 100%;
    max-width: 100%;
  }
  .bemerkung-grid {
    gap: 0;
  }
  #machine-section {
    padding-top: 0;
  }
}
</style>
